import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motionOn } from "./env";
import { onReveal } from "./reveal";

// Mounted once in LangLayout, so every page (landing and legal) marks motion ready for the
// head-script fail-safe. Observes every [data-r] element: on first intersection it adds `in`
// (CSS does the transition) and, for elements with data-play, dispatches `ns:play` so the
// widget can run its sequence. Re-scans after client-side navigation (pathname change).
// Parent effects run after child effects, so widgets' ns:play listeners are already attached.
export function useRevealObserver(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    window.__nsMotionReady = true;
    if (!motionOn()) return;

    let io: IntersectionObserver | null = null;
    const off = onReveal(() => {
      io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            const el = e.target as HTMLElement;
            el.classList.add("in");
            io?.unobserve(el);
            const play = el.dataset.play;
            if (play) el.dispatchEvent(new CustomEvent("ns:play", { detail: play }));
          });
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0.12 },
      );
      document
        .querySelectorAll<HTMLElement>("[data-r]:not(.in)")
        .forEach((el) => io?.observe(el));
    });

    return () => {
      off();
      io?.disconnect();
    };
  }, [pathname]);
}
